import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { searchUsers } from '../services';

export default function UserSearchSelect({ value = [], onChange, multiple = true, placeholder = 'Tìm theo username hoặc email...' }) {
  const [keyword, setKeyword] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!keyword.trim()) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchUsers(keyword.trim(), 10);
        setSuggestions(results);
      } catch {
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [keyword]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(user) {
    if (multiple) {
      if (value.some((item) => item.id === user.id)) return;
      onChange([...value, user]);
    } else {
      onChange([user]);
    }
    setKeyword('');
    setSuggestions([]);
    setIsOpen(false);
  }

  function handleRemove(userId) {
    onChange(value.filter((item) => item.id !== userId));
  }

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1.5 focus-within:border-cyan-400">
        {value.map((user) => (
          <span
            key={user.id}
            className="flex items-center gap-1 rounded-md bg-cyan-50 px-2 py-1 text-xs text-cyan-800"
          >
            {user.username}
            <button
              type="button"
              onClick={() => handleRemove(user.id)}
              aria-label={`Bỏ chọn ${user.username}`}
              className="text-cyan-600 hover:text-cyan-900"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          value={keyword}
          onChange={(event) => {
            setKeyword(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={value.length && !multiple ? '' : placeholder}
          className="min-w-[120px] flex-1 border-none px-1 py-0.5 text-sm outline-none placeholder:text-slate-400"
        />
      </div>

      {isOpen && keyword.trim() && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-cyan-100 bg-white shadow-lg">
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-slate-500">Đang tìm...</div>
          ) : suggestions.length ? (
            suggestions.map((user) => (
              <button
                type="button"
                key={user.id}
                onClick={() => handleSelect(user)}
                className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-slate-50"
              >
                <span className="font-medium text-slate-900">{user.username}</span>
                <span className="text-xs text-slate-500">{user.email}</span>
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-slate-500">Không tìm thấy user.</div>
          )}
        </div>
      )}
    </div>
  );
}
